import { forwardRef, useImperativeHandle } from "react"
import { toast } from "react-toastify"
import clsx from "clsx"
import { useFormik } from "formik"
import * as Yup from "yup"

import { useTranslation } from "react-i18next"

import { InputField } from "components/inputs/InputField"

import { useIsMounted } from "hooks/useIsMounted"

import { userService } from "services/user/user.service"

import { formatFiatCurrency } from "util/formatter"

import { TabHandle } from "./CustomBonusTab"

const RELOAD_DAYS = [7, 14, 21] as const
type ReloadDays = (typeof RELOAD_DAYS)[number]

interface ReloadFormProps {
  userId: string
  onSuccess: () => void
  onClose: () => void
}

export const ReloadForm = forwardRef<TabHandle, ReloadFormProps>(({ userId, onSuccess, onClose }, ref) => {
  const isMounted = useIsMounted()
  const { t } = useTranslation("translation", { useSuspense: false })

  const formik = useFormik({
    initialValues: { amount: 0, days: 7 as ReloadDays },
    enableReinitialize: true,
    validationSchema: Yup.object().shape({
      amount: Yup.number().required().min(100, t("addUserBonus.reloadAmountMin")),
      days: Yup.number()
        .required()
        .oneOf([...RELOAD_DAYS], t("addUserBonus.reloadDaysValidation"))
    }),
    onSubmit: async (values, helpers) => {
      const result = await userService.assignReload({
        userId,
        amount: values.amount,
        days: values.days
      })
      if (!result) {
        if (isMounted()) {
          helpers.setStatus({ success: false })
          helpers.setSubmitting(false)
        }
        return
      }
      toast.success(String(t("addUserBonus.reloadAssignedSuccess")))
      onSuccess()
      onClose()
    }
  })

  useImperativeHandle(ref, () => ({
    handleSubmit: () => formik.handleSubmit(),
    isSubmitting: formik.isSubmitting
  }))

  return (
    <div className="flex flex-col gap-4">
      <InputField
        name="amount"
        type="number"
        title={t("addUserBonus.reloadAmountLabel")}
        value={formik.values.amount}
        onChange={formik.handleChange}
        altLabelOne={formatFiatCurrency(formik.values.amount, "$0.00")}
        invalid={!!(formik.errors.amount && formik.touched.amount)}
        altLabelTwo={formik.touched.amount ? formik.errors.amount : ""}
      />
      <div className="form-control">
        <label className="label">
          <span className="label-text">{t("addUserBonus.reloadDuration")}</span>
        </label>
        <div className="btn-group w-full">
          {RELOAD_DAYS.map((d) => (
            <button
              key={d}
              type="button"
              className={clsx("btn flex-1", { "btn-active": formik.values.days === d })}
              onClick={() => formik.setFieldValue("days", d)}
            >
              {t("addUserBonus.reloadDaysLabel", { count: d })}
            </button>
          ))}
        </div>
        {formik.touched.days && formik.errors.days && (
          <label className="label">
            <span className="label-text-alt text-error">{formik.errors.days}</span>
          </label>
        )}
      </div>
      <div className="bg-base-200 rounded-lg p-3 text-sm">
        {t("addUserBonus.reloadPreview", {
          dailyAmount: formatFiatCurrency(Math.floor(formik.values.amount / formik.values.days), "$0.00"),
          days: formik.values.days
        })}
      </div>
    </div>
  )
})

ReloadForm.displayName = "ReloadForm"
